/**
 * Authentication Helper Functions (Amplify v6)
 *
 * These helpers check authentication states based on Amplify v6 API responses.
 * Used across the application in components, composables, and router guards.
 */
export const authHelpers = {
  /**
   * Check if user needs to complete MFA setup
   *
   * Custom attribute 'custom:mfa_setup' = 'true' indicates first-time MFA setup required.
   * After setup completes, this is set to 'false'.
   *
   * @param {Object} userAttributes - User attributes from fetchUserAttributes()
   * @returns {boolean} True if MFA setup is required
   */
  needsMFASetup: (userAttributes) =>
    userAttributes?.['custom:mfa_setup'] === 'true',

  /**
   * Check if user needs to verify MFA code during login
   *
   * When user has TOTP enabled, Cognito requires code verification.
   * This happens AFTER password is validated.
   *
   * @param {Object} signInResult - Result from signIn()
   * @returns {boolean} True if TOTP code verification is required
   */
  needsMFAVerification: (signInResult) =>
    signInResult?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',

  /**
   * Check if user needs to change their password
   *
   * Happens when user has a temporary password (first login, password reset, etc).
   * User must change password before proceeding.
   *
   * @param {Object} signInResult - Result from signIn()
   * @returns {boolean} True if password change is required
   */
  needsPasswordChange: (signInResult) =>
    signInResult?.nextStep?.signInStep ===
    'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
};
